import { useEffect, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { formatNumber } from "@/lib/helpers/formatNumber"
import { useAppContext } from "@/lib/contexts/appContext"
import CheckmarkCircle from "@/common/partials/checkmarkCircle"
import { Checkbox } from "@/common/form/checkbox"
import { termsCheckbox } from "../payment/paymentMethod.module.css"
import useCheckoutContext, {
  useActiveStep,
  useMinAmount,
  usePaymentAmount,
  useStepByName,
  useCampaignName,
  useMembershipAmount
} from "../store"
import SubmitStepButton from "../user-details/submitStepButton"
import {
  amount as amountStyles,
  selectedAmount as selectedAmountStyles
} from "./amount.module.css"
import CustomValue from "./customValue"
import { addOrderLog } from "@/lib/api/checkoutForm"

function useCurrentAmount() {
  const {
    paymentAmount: { amountValue, hasSubmittedPaymentStep }
  } = usePaymentAmount()
  const { amounts, optional } = useActiveStep()
  let amount = amounts.find((amountObj) => amountObj.default)

  if (amountValue && (!optional || hasSubmittedPaymentStep)) {
    amount = amountValue.toString()
  } else if (amount) {
    amount = amount.amount.toString()
  } else {
    amount = amounts[0].amount.toString()
  }

  return amount
}

function useCustomValue() {
  const {
    paymentAmount: { amountValue }
  } = usePaymentAmount()
  const { amounts } = useActiveStep()

  const membershipStep = useStepByName("membershipType")

  let customValue = null

  customValue =
    amountValue &&
    !amounts.find((amountObj) => amountObj.amount === amountValue)
      ? amountValue
      : null

  if (
    [
      +membershipStep?.productPrices.family_member,
      +membershipStep?.productPrices.youth_member,
      +membershipStep?.productPrices.member
    ].includes(customValue)
  ) {
    customValue = null
  }

  return customValue || null
}

function Amount({ defaultValues, orderSessionId }) {
  const { paymentAmount, setPaymentAmount } = usePaymentAmount()
  const { amountValue, hasSubmittedPaymentStep, skipAmount } = paymentAmount
  const { amounts, allowCustomAmount, haveMembershipAmount } = useActiveStep()
  let minAmount = useMinAmount()
  const { membershipAmount } = useMembershipAmount()

  if (defaultValues?.amount) {
    minAmount = defaultValues.amount
  }

  const {
    paymentAmount: appPaymentAmount,
    setPaymentAmount: setAppPaymentAmount,
    setHasUsedAmount,
    hasUsedAmount,
    setSkipAmountStep,
    campaignName: appCampaignName
  } = useAppContext()

  const { setCampaignName } = useCampaignName()

  let amount = useCurrentAmount()
  let customValue = useCustomValue()

  const methods = useForm({
    defaultValues: {
      amount,
      customValue,
      skipAmountCheckbox: skipAmount
    },
    shouldUnregister: true
  })

  const { setNextActiveStep } = useCheckoutContext()
  const [isCustomValue, setIsCustomValue] = useState(false)
  const currentSelectedAmount = methods.watch("amount")
  const skipAmountCheckbox = methods.watch("skipAmountCheckbox")
  const customValueWatch = methods.watch("customValue")
  const [isZeroAmount, setIsZeroAmount] = useState(false)

  // Clear radio buttons when selecting custom value
  useEffect(() => {
    if (!isCustomValue) {
      return
    }
    setIsZeroAmount(false)
    methods.setValue("amount", "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomValue])

  useEffect(() => {
    if (!appPaymentAmount || hasUsedAmount) {
      return
    }
    methods.setValue("amount", appPaymentAmount, { shouldValidate: true })

    if (!amounts.find((amountObj) => +amountObj.amount === +appPaymentAmount)) {
      methods.setValue("customValue", appPaymentAmount)
      setIsCustomValue(true)
    }

    setHasUsedAmount()
    onSubmit({ amount: appPaymentAmount, customValue: appPaymentAmount })
    setAppPaymentAmount(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appPaymentAmount])

  // Deselect custom value when the amount changes to a preset value
  useEffect(() => {
    if (
      isCustomValue &&
      amounts.find((amountObj) => +amountObj.amount === +currentSelectedAmount)
    ) {
      setIsCustomValue(false)
      setIsZeroAmount(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedAmount])

  useEffect(() => {
    if (skipAmountCheckbox) {
      setIsCustomValue(false)
    }
  }, [skipAmountCheckbox])

  // Maybe set custom value to true on first render
  useEffect(() => {
    if (defaultValues.amount !== undefined) {
      methods.setValue("amount", defaultValues.amount)
      if (
        !amounts.find((amountObj) => amountObj.amount === defaultValues.amount)
      ) {
        setIsCustomValue(true)
        methods.setValue("customValue", defaultValues.amount)
      }
    }

    if (hasSubmittedPaymentStep) {
      let newValue = parseInt(amountValue) - parseInt(membershipAmount)

      methods.setValue("amount", newValue)

      if (!newValue) {
        setIsZeroAmount(true)
      } else {
        if (!amounts.find((amountObj) => +amountObj.amount === +newValue)) {
          setIsCustomValue(true)
          methods.setValue("customValue", newValue)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = ({ amount, customValue }) => {
    if (haveMembershipAmount) {
      let newAmount = 0

      if (isZeroAmount) {
        newAmount = parseInt(membershipAmount)
      } else {
        if (isCustomValue) {
          newAmount = parseInt(membershipAmount) + parseInt(customValue)
        } else {
          newAmount = parseInt(membershipAmount) + parseInt(amount)
        }
      }
      setPaymentAmount(newAmount, true, true)
    } else {
      // Old amount
      setPaymentAmount(isCustomValue ? customValue : amount, true)
    }

    addOrderLog(
      orderSessionId,
      "Valt belopp",
      isCustomValue ? customValue : amount
    )

    setCampaignName(appCampaignName)
    setSkipAmountStep(true)
    setNextActiveStep()
  }

  const deselectSkipAmountCheckbox = () => {
    setIsZeroAmount(false)
    methods.setValue("skipAmountCheckbox", false)
    methods.trigger("skipAmountCheckbox")
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {haveMembershipAmount && (
          <div className="font-bold mb-2">Ja, jag vill stödja med:</div>
        )}

        <div
          className={
            haveMembershipAmount
              ? "flex flex-wrap flex-col mb-6 font-bold border border-b-0 rounded border-black-100/20 mt-4"
              : "flex flex-wrap mb-6 font-bold border divide-y rounded border-black-100/20 divide lg:divide-y-0 lg:divide-x"
          }>
          {amounts.map((amountObj, i) => (
            <AmountItem
              key={i}
              amount={amountObj.amount}
              customText={
                haveMembershipAmount
                  ? "+ " + amountObj.amount + " kr/månad"
                  : false
              }
              isSelected={
                +currentSelectedAmount === +amountObj.amount &&
                !skipAmountCheckbox &&
                !isZeroAmount
              }
              onClick={() => deselectSkipAmountCheckbox()}
              haveMembershipAmount={haveMembershipAmount}
            />
          ))}
        </div>
        {allowCustomAmount && (
          <div className="mb-6">
            {isCustomValue && !skipAmountCheckbox && (
              <CustomValue minAmount={minAmount} />
            )}
            <label
              htmlFor="customValue"
              className={`flex items-center px-4 xl:px-6 py-4 text-lg font-bold border border-black-100/20 rounded cursor-pointer group ${
                isCustomValue && !skipAmountCheckbox ? "hidden" : ""
              }`}
              onClick={() => {
                deselectSkipAmountCheckbox()
                setIsCustomValue(true)
              }}>
              <CheckmarkCircle />
              Valfritt
            </label>
          </div>
        )}

        {haveMembershipAmount && (
          <>
            <div className="mb-6 font-bold border border-b-0 rounded border-black-100/20">
              <AmountItem
                key="zeroAmount"
                customText="Jag vill inte ge en extra månadsgåva"
                amount={0}
                isSelected={isZeroAmount}
                onClick={() => {
                  deselectSkipAmountCheckbox()
                  setIsCustomValue(false)
                  setIsZeroAmount(true)
                  setPaymentAmount(0, true, true)
                }}
                haveMembershipAmount={haveMembershipAmount}
              />
            </div>

            <div className="flex w-full flex-col">
              <div className="flex w-full justify-between">
                <div>Medlemsavgift</div>
                <div>{membershipAmount} kr</div>
              </div>

              <div className="flex w-full justify-between">
                <div>Extra månadsgåva</div>
                <div>
                  {isZeroAmount && <>0 kr</>}

                  {!isZeroAmount && !isCustomValue && (
                    <>{formatNumber(currentSelectedAmount)} kr</>
                  )}

                  {!isZeroAmount && customValueWatch && isCustomValue && (
                    <>{formatNumber(customValueWatch)} kr</>
                  )}
                </div>
              </div>

              <div className="flex w-full justify-between my-4 border-t pt-4">
                <div>Ditt månadsbelopp</div>
                <div className="text-3xl font-bold">
                  {isZeroAmount && <>{membershipAmount} kr</>}

                  {!isZeroAmount && !isCustomValue && (
                    <>
                      {formatNumber(
                        parseInt(currentSelectedAmount) +
                          parseInt(membershipAmount)
                      )}{" "}
                      kr
                    </>
                  )}

                  {!isZeroAmount && customValueWatch && isCustomValue && (
                    <>
                      {formatNumber(
                        parseInt(customValueWatch) + parseInt(membershipAmount)
                      )}{" "}
                      kr
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* {optional && <SkipAmount />} */}
        <SubmitStepButton />
      </form>
    </FormProvider>
  )
}

export function SkipAmount() {
  const { register, watch } = useFormContext()
  const skipAmount = watch("skipAmountCheckbox")
  return (
    <label
      htmlFor="skipAmountCheckbox"
      className="flex items-center cursor-pointer select-none mb-7">
      <input
        type="checkbox"
        {...register("skipAmountCheckbox")}
        id="skipAmountCheckbox"
        className={`${termsCheckbox} absolute -z-1`}
      />
      <Checkbox
        checked={
          skipAmount === true ||
          (skipAmount?.length === 1 && skipAmount[0] === "on")
        }
      />
      <p className="text-sm md:text-base wysiwyg-content">
        Jag vill inte ge en gåva
      </p>
    </label>
  )
}

export function AmountItem({
  amount,
  isSelected,
  id = "amount",
  customText = false,
  haveMembershipAmount,
  onClick = () => {}
}) {
  const { register } = useFormContext()

  const isSelectedClassNames = isSelected
    ? `amount-label bg-green-25 color-white selected-amount relative ${selectedAmountStyles}`
    : "amount-label"

  return (
    <label
      htmlFor={`${id}-${amount}`}
      className={[
        `w-full lg:w-auto flex flex-grow px-4 xl:px-6 py-4 cursor-pointer items-center text-lg group border-black-100/20`,
        isSelectedClassNames,
        haveMembershipAmount ? "border-b " : ""
      ].join(" ")}>
      <input
        type="radio"
        id={`${id}-${amount}`}
        className={`${amountStyles} absolute -z-1`}
        {...register("amount")}
        defaultValue={amount}
        onClick={onClick}
      />
      <CheckmarkCircle selected={isSelected} />
      {customText && <>{customText}</>}

      {!customText && <>{formatNumber(amount)} kr</>}
    </label>
  )
}

export default Amount
